/**
 * Insurance API Service
 *
 * API calls for the Insurance Lead Queue module.
 * This module uses a separate API Gateway from the main forecast API.
 */

import {
  InsuranceLead,
  LeadQueryParams,
  LeadUpdateRequest,
  InsuranceStats,
  LeadsResponse,
} from '../types/insurance';

// Insurance API URL - separate from main API
// Production URL for the Insurance Lead Queue API Gateway
const INSURANCE_API_URL = process.env.REACT_APP_INSURANCE_API_URL || 'https://3lc84tv5mj.execute-api.us-east-1.amazonaws.com';

// Token getter function - set by AuthContext
let getToken: ((forceRefresh?: boolean) => Promise<string | null>) | null = null;

/**
 * Set the token getter function
 * Called by AuthContext to connect authentication
 */
export function setInsuranceTokenGetter(getter: (forceRefresh?: boolean) => Promise<string | null>) {
  getToken = getter;
}

/**
 * API response wrapper
 */
interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

/**
 * Get authorization headers
 */
async function getAuthHeaders(): Promise<Record<string, string>> {
  const token = getToken ? await getToken() : null;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

/**
 * Handle API response with retry on 401
 */
async function handleResponse<T>(
  response: Response,
  retryFn: () => Promise<Response>
): Promise<ApiResponse<T>> {
  // If unauthorized, try to refresh token and retry once
  if (response.status === 401 && getToken) {
    console.log('Token expired, refreshing...');
    const newToken = await getToken(true); // Force refresh
    if (newToken) {
      const retryResponse = await retryFn();
      if (retryResponse.ok) {
        return await retryResponse.json();
      }
    }
    return { success: false, error: 'Authentication failed. Please log in again.' };
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    return { success: false, error: error.error || `HTTP ${response.status}` };
  }

  return await response.json();
}

// ========================================
// LEADS API
// ========================================

/**
 * Get insurance leads with filtering and pagination
 */
export async function getInsuranceLeads(
  params: LeadQueryParams = {}
): Promise<ApiResponse<LeadsResponse>> {
  try {
    const headers = await getAuthHeaders();

    // Build query string
    const queryParams = new URLSearchParams();
    if (params.status) queryParams.set('status', params.status);
    if (params.locationId) queryParams.set('locationId', params.locationId.toString());
    if (params.myLeadsOnly) queryParams.set('myLeadsOnly', 'true');
    if (params.limit) queryParams.set('limit', params.limit.toString());
    if (params.nextToken) queryParams.set('nextToken', params.nextToken);

    const queryString = queryParams.toString();
    const url = `${INSURANCE_API_URL}/insurance/leads${queryString ? `?${queryString}` : ''}`;

    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    return handleResponse<LeadsResponse>(response, () =>
      fetch(url, { method: 'GET', headers })
    );
  } catch (error: any) {
    console.error('Error fetching insurance leads:', error);
    return { success: false, error: error.message || 'Failed to fetch leads' };
  }
}

/**
 * Get a single lead by ID
 */
export async function getInsuranceLead(
  leadId: string
): Promise<ApiResponse<InsuranceLead>> {
  try {
    const headers = await getAuthHeaders();
    const url = `${INSURANCE_API_URL}/insurance/leads/${leadId}`;

    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    return handleResponse<InsuranceLead>(response, () =>
      fetch(url, { method: 'GET', headers })
    );
  } catch (error: any) {
    console.error('Error fetching insurance lead:', error);
    return { success: false, error: error.message || 'Failed to fetch lead' };
  }
}

/**
 * Claim a lead
 */
export async function claimLead(
  leadId: string
): Promise<ApiResponse<InsuranceLead>> {
  try {
    const headers = await getAuthHeaders();
    const url = `${INSURANCE_API_URL}/insurance/leads/${leadId}/claim`;

    const response = await fetch(url, {
      method: 'POST',
      headers,
    });

    return handleResponse<InsuranceLead>(response, () =>
      fetch(url, { method: 'POST', headers })
    );
  } catch (error: any) {
    console.error('Error claiming lead:', error);
    return { success: false, error: error.message || 'Failed to claim lead' };
  }
}

/**
 * Unclaim (release) a lead
 */
export async function unclaimLead(
  leadId: string
): Promise<ApiResponse<InsuranceLead>> {
  try {
    const headers = await getAuthHeaders();
    const url = `${INSURANCE_API_URL}/insurance/leads/${leadId}/unclaim`;

    const response = await fetch(url, {
      method: 'POST',
      headers,
    });

    return handleResponse<InsuranceLead>(response, () =>
      fetch(url, { method: 'POST', headers })
    );
  } catch (error: any) {
    console.error('Error unclaiming lead:', error);
    return { success: false, error: error.message || 'Failed to unclaim lead' };
  }
}

/**
 * Update a lead (notes, status, manual entry)
 */
export async function updateLead(
  leadId: string,
  updates: LeadUpdateRequest
): Promise<ApiResponse<InsuranceLead>> {
  try {
    const headers = await getAuthHeaders();
    const url = `${INSURANCE_API_URL}/insurance/leads/${leadId}`;

    const response = await fetch(url, {
      method: 'PUT',
      headers,
      body: JSON.stringify(updates),
    });

    return handleResponse<InsuranceLead>(response, () =>
      fetch(url, { method: 'PUT', headers, body: JSON.stringify(updates) })
    );
  } catch (error: any) {
    console.error('Error updating lead:', error);
    return { success: false, error: error.message || 'Failed to update lead' };
  }
}

/**
 * Mark a lead as completed
 */
export async function completeLead(
  leadId: string,
  outcome: 'quoted_purchased' | 'quoted_not_purchased' | 'bad_lead',
  notes?: string
): Promise<ApiResponse<InsuranceLead>> {
  return updateLead(leadId, {
    status: 'completed',
    outcome,
    notes,
  });
}

/**
 * Discard a lead
 */
export async function discardLead(
  leadId: string,
  discardReason: string
): Promise<ApiResponse<InsuranceLead>> {
  return updateLead(leadId, {
    status: 'discarded',
    discardReason,
  });
}

/**
 * Delete a lead (hard delete - for leads added in error)
 */
export async function deleteLead(
  leadId: string
): Promise<ApiResponse<void>> {
  try {
    const headers = await getAuthHeaders();
    const url = `${INSURANCE_API_URL}/insurance/leads/${leadId}`;

    const response = await fetch(url, {
      method: 'DELETE',
      headers,
    });

    return handleResponse<void>(response, () =>
      fetch(url, { method: 'DELETE', headers })
    );
  } catch (error: any) {
    console.error('Error deleting lead:', error);
    return { success: false, error: error.message || 'Failed to delete lead' };
  }
}

// ========================================
// STATS API
// ========================================

/**
 * Get queue statistics
 */
export async function getInsuranceStats(): Promise<ApiResponse<InsuranceStats>> {
  try {
    const headers = await getAuthHeaders();
    const url = `${INSURANCE_API_URL}/insurance/stats`;

    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    return handleResponse<InsuranceStats>(response, () =>
      fetch(url, { method: 'GET', headers })
    );
  } catch (error: any) {
    console.error('Error fetching insurance stats:', error);
    return { success: false, error: error.message || 'Failed to fetch stats' };
  }
}

// ========================================
// REPORTS API
// ========================================

/**
 * Archived leads response for reporting
 */
export interface ArchivedLeadsResponse {
  leads: InsuranceLead[];
  totalCount: number;
  dateRange: {
    startDate: string;
    endDate: string;
  };
}

/**
 * Get archived leads for reporting (completed/discarded within date range)
 */
export async function getArchivedLeads(
  startDate: string,
  endDate: string,
  locationId?: number
): Promise<ApiResponse<ArchivedLeadsResponse>> {
  try {
    const headers = await getAuthHeaders();

    const queryParams = new URLSearchParams();
    queryParams.set('startDate', startDate);
    queryParams.set('endDate', endDate);
    if (locationId) queryParams.set('locationId', locationId.toString());

    const url = `${INSURANCE_API_URL}/insurance/reports/archived?${queryParams.toString()}`;

    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    return handleResponse<ArchivedLeadsResponse>(response, () =>
      fetch(url, { method: 'GET', headers })
    );
  } catch (error: any) {
    console.error('Error fetching archived leads:', error);
    return { success: false, error: error.message || 'Failed to fetch archived leads' };
  }
}

// ========================================
// TESTING API (Admin only)
// ========================================

/**
 * Test DealerBuilt matching
 */
export async function testDealerBuiltMatch(
  stockNumber: string,
  locationId: number,
  customerName?: string,
  phone?: string
): Promise<ApiResponse<any>> {
  try {
    const headers = await getAuthHeaders();
    const url = `${INSURANCE_API_URL}/insurance/match`;

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        stockNumber,
        locationId,
        customerName,
        phone,
      }),
    });

    return handleResponse<any>(response, () =>
      fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ stockNumber, locationId, customerName, phone }),
      })
    );
  } catch (error: any) {
    console.error('Error testing DealerBuilt match:', error);
    return { success: false, error: error.message || 'Failed to test match' };
  }
}
